import type { IconNames } from '@repo/foundations';
import { ActionBlockType } from '../../pages/AutomationPage/V2/store';

export const getActionBlockTypeLabel = (
	actionBlockType: ActionBlockType
): string => {
	switch (actionBlockType) {
		case ActionBlockType.FILTER_RESOURCES:
			return 'Filter resources';
		case ActionBlockType.EDIT_RESOURCES:
			return 'Edit resources';
		case ActionBlockType.PROPAGATE_METADATA:
			return 'Propagate metadata';
		case ActionBlockType.SEND_ANNOUNCEMENT:
			return 'Send announcement';
		case ActionBlockType.SEND_SLACK_MESSAGE:
			return 'Send Slack message';
		case ActionBlockType.SEND_EMAIL:
			return 'Send email';
		case ActionBlockType.GENERATE_AI_OUTPUT:
			return 'Generate AI output';
		default:
			throw new Error(`Unknown action block type: ${actionBlockType}`);
	}
};

export const getActionBlockTypeIcon = (
	actionBlockType: ActionBlockType
): IconNames => {
	switch (actionBlockType) {
		case ActionBlockType.FILTER_RESOURCES:
			return 'filter';
		case ActionBlockType.EDIT_RESOURCES:
			return 'pencil';
		case ActionBlockType.PROPAGATE_METADATA:
			return 'cornerDownRightDouble';
		case ActionBlockType.SEND_ANNOUNCEMENT:
			return 'speakerphone';
		case ActionBlockType.SEND_SLACK_MESSAGE:
			return 'brandSlack';
		case ActionBlockType.SEND_EMAIL:
			return 'mail';
		case ActionBlockType.GENERATE_AI_OUTPUT:
			return 'sparkles';
		default:
			throw new Error(`Unknown action block type: ${actionBlockType}`);
	}
};

export const getActionBlockTypeDescription = (
	actionBlockType: ActionBlockType
): string | undefined => {
	switch (actionBlockType) {
		case ActionBlockType.FILTER_RESOURCES:
			return 'Filter for when conditions are met';
		case ActionBlockType.EDIT_RESOURCES:
			return 'Update metadata fields such as descriptions, tags, owners';
		case ActionBlockType.PROPAGATE_METADATA:
			return 'Copy fields upstream or downstream';
		case ActionBlockType.GENERATE_AI_OUTPUT:
			return 'Create an AI output that you can then connect to your resources';
		default:
			return undefined;
	}
};

/**
 * Shared utility function to format automation actions for display
 * Used by both AutomationHistoryDetailsTable and AutomationS3ResourceTable
 */
export function formatAutomationAction(
	actionType: string,
	value: string | string[]
): string {
	const stringOr = (s: string | string[], d: string) =>
		!!s && s.length > 0 ? s : d;

	switch (actionType) {
		// Owners
		case 'owners_add':
			return `Add ${value} as owner`;
		case 'owners_remove':
			return `Remove ${value} as owner`;
		case 'owners_replace':
			return `Replace owners with ${value}`;
		case 'owners_clear':
			return 'Clear all owners';
		case 'owners_set':
		case 'owners_groups_set':
			return `Set owners to ${stringOr(value, '[]')}`;

		// Teams
		case 'teams_add':
			return `Add team ${value}`;
		case 'teams_remove':
			return `Remove team ${value}`;
		case 'teams_replace':
			return `Replace teams with ${value}`;
		case 'teams_clear':
			return 'Clear all teams';
		case 'teams_add_all':
			return 'Add all teams';
		case 'teams_set':
			return `Set teams to ${stringOr(value, '[]')}`;

		// Collections
		case 'collections_add':
			return `Add to collection ${value}`;
		case 'collections_remove':
			return `Remove from collection ${value}`;
		case 'collections_replace':
			return `Set collections to ${value}`;
		case 'collections_clear':
			return 'Clear all collections';
		case 'collections_set':
			return `Set collections to ${stringOr(value, '[]')}`;

		// Subscribers
		case 'subscribers_add':
			return `Add ${value} as subscriber`;
		case 'subscribers_remove':
			return `Remove ${value} as subscriber`;
		case 'subscribers_replace':
			return `Replace subscribers with ${value}`;
		case 'subscribers_clear':
			return 'Clear all subscribers';
		case 'subscribers_set':
		case 'subscribers_groups_set':
			return `Set subscribers to ${stringOr(value, '[]')}`;

		// Tags
		case 'tags_add':
			return `Add tags ${value}`;
		case 'tags_remove':
			return `Remove tags ${value}`;
		case 'tags_replace':
			return `Replace tags with ${value}`;
		case 'tags_clear':
			return 'Clear all tags';
		case 'tags_set':
			return `Set tags to ${stringOr(value, '[]')}`;

		// Definition
		case 'definition_set':
			return `Set definition to ${value}`;
		case 'definition_clear':
			return 'Clear definition';

		// Description
		case 'description_set':
			return `Set description to ${value}`;
		case 'description_clear':
			return 'Clear description';

		// Governance - PII
		case 'pii_true':
			return 'Set PII to true';
		case 'pii_false':
			return 'Set PII to false';

		// Governance - Verified
		case 'verified_true':
			return 'Set verified to true';
		case 'verified_false':
			return 'Set verified to false';

		// Status - Published
		case 'published_true':
			return 'Set published to true';
		case 'published_false':
			return 'Set published to false';

		default:
			return `Unknown action: ${actionType}`;
	}
}

export function formatAutomationActivityLog(
	field: string,
	value: unknown
): string {
	switch (field) {
		case 'pii':
			return `Set PII to ${value}`;
		case 'verified':
			return `Set verified to ${value}`;
		case 'published':
			return `Set published to ${value}`;
		case 'tag_ids':
			return `added tags`;
		case 'description':
			return `Set description to ${value}`;
		case 'definition':
			return `Set documentation to ${value}`;
		case 'new_columns':
			if (Array.isArray(value) && value.length === 0) return '';
			return `New columns detected: ${Array.isArray(value) ? value.join(', ') : value}`;
		case 'deleted_columns':
			if (Array.isArray(value) && value.length === 0) return '';
			return `Deleted columns detected: ${Array.isArray(value) ? value.join(', ') : value}`;
		default:
			return `Unknown field: ${field}`;
	}
}
